// Shared validation helpers for investment declaration fields (employee + admin)

const INT32_MAX = 2147483647;

export const getNumberRangeError = (label, rawValue) => {
  if (rawValue === '' || rawValue == null) return '';
  const num = Number(rawValue);
  if (!Number.isFinite(num)) return `${label} must be a valid number`;
  if (num < 0) return `${label} cannot be negative`;
  if (num > INT32_MAX) return `${label} is too large (max ${INT32_MAX})`;
  return '';
};

export const getPanError = (label, rawValue) => {
  const v = (rawValue || '').toString().trim().toUpperCase();
  if (!v) return '';
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  if (panRegex.test(v)) return '';
  // Common mix-up with TAN format
  if (/^[A-Z]{4}[0-9]{5}[A-Z]$/.test(v)) {
    return `${label} looks like a TAN. Use PAN format ABCDE1234F (5 letters, 4 digits, 1 letter).`;
  }
  return `Please enter a valid ${label} (e.g., ABCDE1234F — 5 letters, 4 digits, 1 letter)`;
};

/** Indian TAN: 4 letters + 5 digits + 1 letter (e.g. ABCD12345E). */
export const getTanError = (label, rawValue) => {
  const v = (rawValue || '').toString().trim().toUpperCase();
  if (!v) return '';
  const tanRegex = /^[A-Z]{4}[0-9]{5}[A-Z]$/;
  if (tanRegex.test(v)) return '';
  // Common mix-up with PAN format (what user entered: ABCDG1525W)
  if (/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v)) {
    return `${label} looks like a PAN. Use TAN format ABCD12345E (4 letters, 5 digits, 1 letter).`;
  }
  return `Please enter a valid ${label} (e.g., ABCD12345E — 4 letters, 5 digits, 1 letter)`;
};

export const getAddressError = (label, rawValue) => {
  const v = (rawValue || '').toString().trim();
  if (!v) return '';
  if (v.length < 3) return `${label} is too short`;
  return '';
};

export const PREV_EMPLOYMENT_NA = 'Not Applicable';

export const employeeRequiresPrevEmploymentDetails = ({
  experiences,
  isFresher,
} = {}) => {
  const hasExperiences = Array.isArray(experiences) && experiences.length > 0;
  const isNotFresher =
    isFresher === false ||
    isFresher === 0 ||
    isFresher === '0' ||
    isFresher === 'false';
  return hasExperiences || isNotFresher;
};

export const isPrevEmploymentAllEmpty = (prevEmployment = {}) => {
  const employerName = (prevEmployment.employerName || '').trim();
  const employerPan = (prevEmployment.employerPan || '').trim();
  const employerTan = (prevEmployment.employerTan || '').trim();
  const grossSalary = (prevEmployment.grossSalary ?? '').toString().trim();
  const taxDeducted = (prevEmployment.taxDeducted ?? '').toString().trim();
  const professionalTax = (prevEmployment.professionalTax ?? '')
    .toString()
    .trim();
  return (
    !employerName &&
    !employerPan &&
    !employerTan &&
    !grossSalary &&
    !taxDeducted &&
    !professionalTax
  );
};

export const normalizePrevEmploymentForSave = (
  prevEmployment = {},
  { applyDefaultsIfEmpty = false } = {}
) => {
  if (applyDefaultsIfEmpty && isPrevEmploymentAllEmpty(prevEmployment)) {
    return {
      prevEmployerName: PREV_EMPLOYMENT_NA,
      prevEmployerPan: null,
      prevEmployerTan: null,
      prevGrossSalary: 0,
      prevTaxDeducted: 0,
      prevProfessionalTax: 0,
    };
  }

  const employerName = (prevEmployment.employerName || '').trim();
  const employerPan = (prevEmployment.employerPan || '').toString().trim().toUpperCase();
  const employerTan = (prevEmployment.employerTan || '').toString().trim().toUpperCase();
  const grossSalaryRaw = (prevEmployment.grossSalary ?? '').toString().trim();
  const taxDeductedRaw = (prevEmployment.taxDeducted ?? '').toString().trim();
  const professionalTaxRaw = (prevEmployment.professionalTax ?? '')
    .toString()
    .trim();

  return {
    prevEmployerName: employerName,
    prevEmployerPan: employerPan || null,
    prevEmployerTan: employerTan || null,
    prevGrossSalary:
      grossSalaryRaw !== '' ? parseFloat(grossSalaryRaw) || 0 : null,
    prevTaxDeducted:
      taxDeductedRaw !== '' ? parseFloat(taxDeductedRaw) || 0 : null,
    prevProfessionalTax:
      professionalTaxRaw !== '' ? parseFloat(professionalTaxRaw) || 0 : 0,
  };
};

export const validatePrevEmploymentFields = (
  prevEmployment = {},
  { required = true } = {}
) => {
  const errors = {};

  if (!required && isPrevEmploymentAllEmpty(prevEmployment)) {
    return errors;
  }

  const employerName = (prevEmployment.employerName || '').trim();
  if (!employerName) {
    errors.prevEmployerName = 'Previous Employer Name is required.';
  } else if (!/^[A-Za-z\s]+$/.test(employerName)) {
    errors.prevEmployerName =
      'Previous Employer Name must contain only letters and spaces.';
  }

  const employerPan = (prevEmployment.employerPan || '').toString().trim();
  if (required && !employerPan) {
    errors.prevEmployerPan = 'PAN is required.';
  } else {
    const panErr = getPanError('PAN', employerPan);
    if (panErr) errors.prevEmployerPan = panErr;
  }

  const employerTan = (prevEmployment.employerTan || '').toString().trim();
  if (employerTan) {
    const tanErr = getTanError('TAN', employerTan);
    if (tanErr) errors.prevEmployerTan = tanErr;
  }

  const grossSalary = (prevEmployment.grossSalary ?? '').toString().trim();
  let grossError = getNumberRangeError(
    'Total Salary Received (Gross)',
    grossSalary
  );
  if (!grossError && !grossSalary) {
    grossError = 'Total Salary Received (Gross) is required.';
  }
  if (grossError) errors.prevGrossSalary = grossError;

  const taxDeducted = (prevEmployment.taxDeducted ?? '').toString().trim();
  let taxError = getNumberRangeError('Total Tax Deducted', taxDeducted);
  if (!taxError && !taxDeducted) {
    taxError = 'Total Tax Deducted is required.';
  }
  if (!taxError && grossSalary && taxDeducted) {
    const gross = parseFloat(grossSalary);
    const tax = parseFloat(taxDeducted);
    if (!Number.isNaN(gross) && !Number.isNaN(tax) && tax >= gross) {
      taxError =
        'Total Tax Deducted must be less than Total Salary Received (Gross).';
    }
  }
  if (taxError) errors.prevTaxDeducted = taxError;

  const professionalTax = (prevEmployment.professionalTax ?? '')
    .toString()
    .trim();
  const ptError = getNumberRangeError(
    'Professional Tax Deducted (Previous Employer)',
    professionalTax
  );
  if (ptError) errors.prevProfessionalTax = ptError;

  return errors;
};
